import React, {useState} from 'react'
import clsx from 'clsx'
import Chart from 'react-apexcharts'
import {getCSSVariableValue} from '@/_metronic/assets/ts/_utils'

type AggKey = 'value' | 'cogs' | 'soldAmount'

type AggProps = {
  slug: AggKey
  label: string
  title: string
}

const aggs: AggProps[] = [
  { label: 'Qty', slug: 'value', title: 'Sold count' },
  { label: 'COGS', slug: 'cogs', title: 'COGS' },
  { label: 'Amount', slug: 'soldAmount', title: 'Sold amount' },
]

type Props<T> = {
  id: string
  title: React.ReactNode
  data: T[]
  displayRow?: boolean;
  height: number
  isLoading: boolean
  getLabel: (item: T) => string
  getColorClass?: (item: T) => string
  showAggs?: boolean
  getAggValue: (item: T, agg: AggKey) => number
}

export function DonutCard<T>({
  id,
  title,
  data,
  height,
  displayRow,
  isLoading,
  getLabel,
  getColorClass,
  getAggValue,
  showAggs = false,
}: Props<T>) {
  const [agg, setAgg] = useState<AggKey>('value')
  const currentAgg = showAggs ? agg : 'value'

  const fallback = ['primary', 'success', 'info', 'danger', 'warning', 'dark']
  const colorClasses = data.map((item, i) => getColorClass?.(item) || fallback[i % fallback.length])

  const series = data.map((item) => getAggValue(item, currentAgg))
  const max = series.reduce((acc, val) => acc + val, 0)
  const labels = data.map((item) => getLabel(item))
  const colors = colorClasses.map((c) => getCSSVariableValue(`--bs-text-${c}`))

  const options = {
    chart: {
      id,
      toolbar: { show: false },
      zoom: { enabled: false },
      sparkline: { enabled: true },
    },
    labels,
    colors,
    legend: { show: false },
  }

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header">
        <div className="card-title">
          <span className="card-label fw-bold fs-3 mb-1">{title}</span>
        </div>
        {showAggs && (
          <div className="pb-5 mt-5">
            {aggs.map((p) => (
              <button
                key={p.slug}
                className={clsx(
                  'btn btn-sm btn-color-muted btn-active btn-active-primary me-3',
                  { active: agg === p.slug }
                )}
                type="button"
                title={p.title}
                onClick={() => setAgg(p.slug)}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="card-body p-9">
        {isLoading ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <i className="spinner-border fs-5x" />
          </div>
        ) : data.length === 0 ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <span>No data</span>
          </div>
        ) : (
          <div className="row align-items-center mx-0 w-100">
            <div className={clsx(
              'col-12 d-flex justify-content-center px-0 mb-4 mb-md-0',
              displayRow !== true ? 'col-md-6' : ''
            )}>
              <Chart options={options} series={series} type="donut" height={height} />
            </div>
            <div className={clsx(
              'col-12 px-0',
              displayRow !== true ? 'col-md-6' : 'mt-8'
            )}>
              <div className="d-flex flex-column justify-content-center">
                {data.map((item, i) => (
                  <div
                    key={i}
                    className="d-flex fs-6 fw-semibold align-items-center mb-2"
                    title={`${getAggValue(item, currentAgg)}`}
                  >
                    <div className={`bullet bg-${colorClasses[i] || 'secondary'} me-3`} />
                    <div className="fs-5 fw-bold text-gray-600 me-5">{getLabel(item)}</div>
                    <div className="ms-auto fw-bolder text-gray-700 text-end">
                      {max > 0 ? ((getAggValue(item, currentAgg) * 100) / max).toFixed(2) : '0.00'}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
